export enum PhotoRelationship {
  Contributor = 'contributor',
  PetOwner = 'pet_owner',
}

export interface GalleryImageUploader {
  name: string;
  profileUrl?: string | null;
  /** The sitter-profile API url the app's native profile screen loads. */
  nativeProfileUrl?: string | null;
}

export interface GalleryImage {
  small: string;
  largeUncropped: string;
  /**
   * Alt text / visible caption for the image, used for accessibility.
   */
  caption?: string;
  /**
   * The image's accessible name, for galleries whose photos have no caption
   * worth showing. It takes precedence over `caption` for assistive tech and,
   * unlike `caption`, is never rendered — so an index ("Photo 3 of Sarah's
   * care") can distinguish otherwise identical tiles without putting a label
   * on screen next to the lightbox's own counter.
   */
  altText?: string;
  /**
   * The date the image was uploaded, formatted as a human-readable string.
   */
  formattedAddedDate?: string;
  relationship?: PhotoRelationship;
  uploader?: GalleryImageUploader | null;
  id?: number | null;
  /**
   * Dimensions of the original upload. Supplying them saves the lightbox a
   * download it would otherwise make purely to measure the photo, so anything
   * mapping an API image should pass them through.
   */
  originalWidth?: number;
  originalHeight?: number;
}

/**
 * A sitter's intro video, sitting in the gallery alongside their photos.
 *
 * It extends `GalleryImage` rather than standing beside it so that `small` and
 * `largeUncropped` carry the video's thumbnail. Everything that already reads
 * those fields — the grid tiles, `useImageGallery`, `useImageSize` — keeps
 * working on a video item without knowing it is one.
 */
export interface GalleryVideoItem extends GalleryImage {
  /**
   * Mirrors the shared player's props, so the item feeds it directly. It is
   * also the discriminant; narrow with `isGalleryVideoItem` rather than by
   * testing for the key yourself.
   */
  video: {
    sources: string[];
    description: string;
  };
}

export type GalleryItem = GalleryImage | GalleryVideoItem;

/**
 * The `undefined` check isn't belt-and-braces: `{ ...image, video: introVideo }`
 * with an optional `introVideo` type-checks as a `GalleryItem`, because excess
 * property checking against a union only rejects keys no member declares. So a
 * key present with an `undefined` value reaches here, and `'video' in item`
 * alone would narrow it to a video whose `sources` don't exist.
 */
export const isGalleryVideoItem = (item: GalleryItem): item is GalleryVideoItem =>
  'video' in item && item.video !== undefined;

/**
 * How many of these items are videos.
 *
 * `PaginationConfig.totalImageCount` is a photo count — the endpoints it comes
 * from know nothing about the video — while `items.length` counts everything on
 * screen. Anything comparing the two has to reconcile the bases first, and this
 * is the term that does it.
 */
export const countGalleryVideoItems = (items: GalleryItem[] | undefined): number =>
  items?.filter(isGalleryVideoItem).length ?? 0;

/**
 * React list key for a gallery item.
 *
 * Videos key off their source rather than a fixed literal so that a gallery
 * holding more than one can't emit duplicate keys — which surfaces as a
 * recycled tile showing the wrong thumbnail, and reads like an image-loading
 * bug rather than a keying one.
 */
export const galleryItemKey = (item: GalleryItem): string =>
  isGalleryVideoItem(item) ? `video-${item.video.sources[0] ?? item.small}` : item.small;

export enum GalleryVisibility {
  All = '',
  Public = 'public',
  Private = 'private',
}

export enum GalleryViewMode {
  Grid = 'GRID',
  Lightbox = 'LIGHTBOX',
}

export type LoadMoreVariant = 'auto' | 'button';

export interface PaginationConfig {
  onLoadMore: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  totalImageCount: number;
  pageSize: number;
  loadMoreVariant?: LoadMoreVariant;
  /** The last page request failed, so a view waiting on it can offer a retry. */
  loadMoreFailed?: boolean;
}

// An item with no id can be shown but never saved.
export const isEditableGalleryImage = (item: GalleryItem | undefined): boolean =>
  !!item && !isGalleryVideoItem(item) && item.relationship !== undefined && item.id != null;

export const isDimmedImage = (
  image: GalleryItem | undefined,
  dimmedImageIds: Set<number> | undefined
): boolean => image?.id != null && !!dimmedImageIds?.has(image.id);

export const isLockedImage = (
  image: GalleryItem | undefined,
  lockedImageIds: Set<number> | undefined
): boolean => image?.id != null && !!lockedImageIds?.has(image.id);
